import { computed, defineAsyncComponent, defineComponent, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  Col as VanCol,
  Icon as VanIcon,
  NavBar as VanNavBar,
  Row as VanRow,
} from 'vant'
import {
  shouldShowLivePlayer,
  useManualToggleLivePlayerHook,
  useWsLiveData,
} from '@/components/lottery-tabs'
import { BorderHome } from '@/components/border-home'
import LiveTopAdBar from '@/components/live-top-ad-bar'
import { useRegionStore } from '@/stores/region-store'
import { useVideoStore } from '@/stores/video-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { lotteryName } from '@/utils/seo'
import type { LotteryType } from '@/stores/region-store'
import type { LiveLotteryData } from '@/api/types.gen'

const LiveStreamPlayer: any = defineAsyncComponent(() => import('@/components/live-stream-player'))

const PLATFORM_LIST: { lotteryType: LotteryType; lotteryName: string }[] = [
  { lotteryType: 'macau', lotteryName: '澳彩' },
  { lotteryType: 'hk', lotteryName: '港彩' },
]

export default defineComponent({
  name: 'LivePage',
  setup() {
    const route = useRoute()
  const goBack = useSmartBack()
    const regionStore = useRegionStore()
    const videoStore = useVideoStore()

    // SEO：开奖现场（直播）页，随彩种动态（站点名取后台配置）
    const brand = useSiteBrand()
    const lotName = computed(() => lotteryName(regionStore.lotteryType))
    useSeo({
      title: () => `${lotName.value}开奖直播_开奖现场视频直播 - ${brand.value}`,
      description: () =>
        `${lotName.value}开奖现场视频直播，实时同步每期开奖号码与开奖时间，开奖结果快速准确。`,
      keywords: () => `${lotName.value},开奖直播,开奖现场,视频直播,实时开奖,${brand.value}`,
    })

    const hkData = ref<LiveLotteryData | null>(null)
    const aocaiData = ref<LiveLotteryData | null>(null)
    const hkDrawingMode = ref(false)
    const aocaiDrawingMode = ref(false)
    const volumeOn = ref(true)

    const { connectBoth } = useWsLiveData(hkData, aocaiData, hkDrawingMode, aocaiDrawingMode)
    const { manualShowLivePlayer, manualToggleLivePlayer } = useManualToggleLivePlayerHook()

    onMounted(() => {
      // 默认澳彩（49208 行为）；url ?type=hk 可显式切港彩
      const t = route.query.type
      regionStore.setLotteryType(t === 'hk' ? 'hk' : 'macau')
      videoStore.fetchAll()
      connectBoth()
    })

    watch(hkData, (d) => {
      if (d && !hkDrawingMode.value && shouldShowLivePlayer(d)) hkDrawingMode.value = true
    })
    watch(aocaiData, (d) => {
      if (d && !aocaiDrawingMode.value && shouldShowLivePlayer(d)) aocaiDrawingMode.value = true
    })

    function handleTabChange(type: LotteryType) {
      if (regionStore.lotteryType === type) return
      regionStore.setLotteryType(type)
    }

    return () => {
      const currentData = regionStore.lotteryType === 'hk' ? hkData.value : aocaiData.value
      const currentDrawingMode =
        regionStore.lotteryType === 'hk' ? hkDrawingMode.value : aocaiDrawingMode.value
      const livePlayerShown = manualShowLivePlayer.value ?? currentDrawingMode

      return (
        <div class="m-scene-recommend">
          <div class="m-scene-recommend-body" style="padding-bottom:10px">
            <VanNavBar title="开奖现场" fixed placeholder>
              {{
                left: () => (
                  <VanIcon name="arrow-left" color="#333" size="16" onClick={() => goBack()} />
                ),
              }}
            </VanNavBar>

            <div style="padding-top:8px">
              <ul class="platform-list">
                {PLATFORM_LIST.map((p) => (
                  <li key={p.lotteryType}>
                    <button
                      type="button"
                      class={['item-body btn-block', { act: p.lotteryType === regionStore.lotteryType }]}
                      onClick={() => handleTabChange(p.lotteryType)}
                    >
                      <span class="title block">
                        <b>{p.lotteryName}</b>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <div class="v-player">
                <VanRow
                  class="home-shad"
                  style="margin-bottom:0;padding-bottom:8px;border-radius:0;background:#fff"
                >
                  <VanCol span={24} style="position:relative">
                    <VanRow>
                      <VanCol span={24} class={{ openLives: !livePlayerShown }}>
                        <div class="video" style="height:240px;background:#000;position:relative">
                          <LiveStreamPlayer lotteryType={regionStore.lotteryType} />
                          {currentDrawingMode && <LiveTopAdBar />}
                          <div style="position:absolute;right:0;bottom:0;z-index:1">
                            <button
                              style="height:26px;padding:0 8px;background:none;border:none;color:#fff;font-size:16px;cursor:pointer"
                              onClick={(e: Event) => {
                                e.stopPropagation()
                                volumeOn.value = !volumeOn.value
                              }}
                            >
                              {volumeOn.value ? '🔊' : '🔇'}
                            </button>
                          </div>
                        </div>
                      </VanCol>
                    </VanRow>
                  </VanCol>
                </VanRow>
                <div class="v-open-hide">
                  {/* 样式挂在 `.v-open-hide span` 上：span 保留，按钮包在外面 */}
                  <button type="button" class="btn-reset" onClick={manualToggleLivePlayer}>
                    <span class={{ open: livePlayerShown }}>
                      <label>{livePlayerShown ? '收起直播' : '展开直播'}</label>
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div class="th">
              <BorderHome lotteryType={regionStore.lotteryType} data={currentData} hideCountdown />
            </div>
          </div>

          <div class="prize-list">
            <div class="hd-box">
              <VanRow>
                <VanCol span={12}>
                  <span class="title">开奖回放</span>
                </VanCol>
                <VanCol span={12} style="text-align:right">
                  <RouterLink class="right btn-reset" to={`/video-list?type=${regionStore.lotteryType}`}>
                    更多开奖回放&gt;&gt;
                  </RouterLink>
                </VanCol>
              </VanRow>
            </div>
            <div class="bd-box">
              <VanRow gutter="20">
                {videoStore.latestFour.map((item) => (
                  <VanCol key={item.id} span={12}>
                    <RouterLink
                      class="item btn-reset block"
                      to={`/video/${item.period}?type=${regionStore.lotteryType}&year=${item.year}`}
                    >
                      <div class="image">
                        {/* referrerpolicy 必须写在 src 之前：Vue 3 patchProps 按 key 插入顺序遍历，
                            否则浏览器解析到 src 时已发请求、referrerpolicy 还没贴到 dom 上 */}
                        <img
                          referrerpolicy="no-referrer"
                          src={item.pictureUrl}
                          alt=""
                          class="w-full h-full object-cover block"
                        />
                      </div>
                      <div class="text">{item.videoTitle}</div>
                    </RouterLink>
                  </VanCol>
                ))}
              </VanRow>
            </div>
          </div>
        </div>
      )
    }
  },
})
